import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Empty, Form, FormInstance, Grid, Input, Modal, notification, Row, Space, Table, Tabs, Tag, Tooltip } from "antd";
import { EyeOutlined, FilePdfOutlined, SearchOutlined } from "@ant-design/icons";
import { FaUniversity, FaFileSignature, FaFileAlt } from "react-icons/fa";
import locale from 'antd/es/locale/pt_BR';
import Highlighter from "react-highlight-words";
import moment from "moment";

import { IBancoVanBancaria, IDocumentoVanBancaria } from "../../../../../../interfaces";
import { ICreateCredenciaisVan, IDeleteAutorizacao, IGetBancosVanBancaria, IGetDocumentoVanSignedUrl, IUpdateCredenciaisVan, IUpdateNumeroRequerimentoVan } from "../../interfaces";
import { ISolicitacaoAtivacao } from "../../../../../interfaces";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import { removeAcento } from "../../../../../../../../utils/RemoveAcentos";
import CardInformacao from "../../../../../../components/CardInformacao";
import TabTitleCount from "../../../../components/TabTitleCount";
import BotaoLimparFiltros, { checkHasActiveFilters } from "../../../../../../components/BotaoLimparFiltros";
import BotaoLimparSelecao from "../../../../../../components/BotaoLimparSelecao";

const { useBreakpoint } = Grid;

interface IProps {
    solicitacao: ISolicitacaoAtivacao | null;
    isManutencao?: boolean;
    bancosVan?: IBancoVanBancaria[] | null;
    isLoading?: boolean;
    onGetBancosVanBancaria?: (data: IGetBancosVanBancaria) => Promise<IBancoVanBancaria[]>;
    onUpdateNumeroRequerimento?: (data: IUpdateNumeroRequerimentoVan) => Promise<IBancoVanBancaria | null>;
    onCreateCredenciais?: (data: ICreateCredenciaisVan) => Promise<IBancoVanBancaria | null>;
    onUpdateCredenciais?: (data: IUpdateCredenciaisVan) => Promise<IBancoVanBancaria | null>;
    onDeleteAutorizacao?: (data: IDeleteAutorizacao) => Promise<void>;
    onGetDocumentoSignedUrl?: (data: IGetDocumentoVanSignedUrl) => Promise<{ url?: string; link?: string } | any>;
    onBancosVanChange?: (bancos: IBancoVanBancaria[]) => void;
}

export const BancosVanBancaria = ({
    solicitacao,
    isManutencao = false,
    bancosVan: bancosVanProps,
    isLoading: isLoadingProps,
    onGetBancosVanBancaria,
    onUpdateNumeroRequerimento,
    onCreateCredenciais,
    onUpdateCredenciais,
    onDeleteAutorizacao,
    onGetDocumentoSignedUrl,
    onBancosVanChange,
}: IProps) => {
    const screens = useBreakpoint();

    const [bancosVan, setBancosVan] = useState<IBancoVanBancaria[] | null>(bancosVanProps || null);
    const [isLoading, setIsLoading] = useState<boolean>(isLoadingProps ?? false);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [hasOneRowSelected, setHasOneRowSelected] = useState<boolean>(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});

    const [isNumeroRequerimentoModalVisible, setIsNumeroRequerimentoModalVisible] = useState<boolean>(false);
    const [isCredenciaisModalVisible, setIsCredenciaisModalVisible] = useState<boolean>(false);
    const [isVisualizarArquivosModalVisible, setIsVisualizarArquivosModalVisible] = useState<boolean>(false);

    const [isSubmittingNumeroRequerimento, setIsSubmittingNumeroRequerimento] = useState<boolean>(false);
    const [isSubmittingCredenciais, setIsSubmittingCredenciais] = useState<boolean>(false);
    const [loadingDocId, setLoadingDocId] = useState<number | null>(null);

    const formNumeroRequerimentoRef = useRef<FormInstance<any>>(null);
    const formCredenciaisRef = useRef<FormInstance<any>>(null);

    useEffect(() => {
        if (bancosVanProps !== undefined) {
            setBancosVan(bancosVanProps);
        }
    }, [bancosVanProps]);

    useEffect(() => {
        if (isLoadingProps !== undefined) {
            setIsLoading(isLoadingProps);
        }
    }, [isLoadingProps]);

    const findData = useCallback(async () => {
        if (!onGetBancosVanBancaria || (!solicitacao && !isManutencao)) return;

        try {
            setIsLoading(true);
            const data = await onGetBancosVanBancaria({
                solicitacaoId: solicitacao?.id,
                orgaoId: solicitacao?.orgao?.id,
            });
            setBancosVan(data);
            if (onBancosVanChange) {
                onBancosVanChange(data);
            }
        } catch (error) {
            EmitirErro(error, true, 'getBancosVanBancaria_tab_error');
        } finally {
            setIsLoading(false);
        }
    }, [onGetBancosVanBancaria, solicitacao, isManutencao, onBancosVanChange]);

    useEffect(() => {
        if (bancosVanProps === undefined && onGetBancosVanBancaria) {
            findData();
        }
    }, [findData, bancosVanProps, onGetBancosVanBancaria]);

    const selectedBanco = useMemo(() => {
        if (selectedRowKeys.length !== 1 || !bancosVan) return null;
        return bancosVan.find((b) => b.id === selectedRowKeys[0]) || null;
    }, [selectedRowKeys, bancosVan]);

    const enableButtonGerenciarCredenciais = useMemo(() => {
        if (!selectedBanco) return false;
        return !!selectedBanco.autorizacaoBancoVan?.numeroRequerimento;
    }, [selectedBanco]);

    const enableButtonVisualizarArquivos = useMemo(() => {
        if (!selectedBanco) return false;
        return !!selectedBanco.autorizacaoBancoVan?.credencialBancoVan;
    }, [selectedBanco]);

    const onSelectChange = useCallback((keys: any[]) => {
        if (keys.length > 1) {
            setSelectedRowKeys([keys[1]]);
            setHasOneRowSelected(true);
        } else if (keys.length === 0) {
            setSelectedRowKeys([]);
            setHasOneRowSelected(false);
        } else {
            setSelectedRowKeys([keys[0]]);
            setHasOneRowSelected(true);
        }
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any,
    };

    const onclickTable = useCallback((record: IBancoVanBancaria) => {
        if (selectedRowKeys.includes(record.id)) {
            setSelectedRowKeys([]);
            setHasOneRowSelected(false);
        } else {
            setSelectedRowKeys([record.id]);
            setHasOneRowSelected(true);
        }
    }, [selectedRowKeys]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasOneRowSelected(false);
    }, []);

    const handleSearch = useCallback((selectedKeys, confirm, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters) => {
        clearFilters();
        setSearchText('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchText('');
        setSearchedColumn('');
    }, []);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchText);
    }, [filteredInfo, searchText]);

    const getColumnSearchProps = useCallback((
        dataIndex: string,
        valueGetter?: (record: any) => string
    ) => {
        let searchInput: any = null;
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={(node) => {
                            searchInput = node;
                        }}
                        placeholder="Digite..."
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: any, record: any) => {
                const recordValue = valueGetter ? valueGetter(record) : (record[dataIndex]?.toString() || '');
                const trecord = removeAcento(recordValue);
                const valueToCompare = removeAcento(value.toString());
                return trecord.includes(valueToCompare);
            },
            onFilterDropdownVisibleChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInput?.select());
                }
            },
            render: (text: any, record: any) => {
                const displayVal = valueGetter ? valueGetter(record) : text;
                return searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={displayVal ? displayVal.toString() : ''}
                    />
                ) : (
                    displayVal
                );
            },
        };
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const handleCloseModals = useCallback(() => {
        setIsNumeroRequerimentoModalVisible(false);
        setIsCredenciaisModalVisible(false);
        setIsVisualizarArquivosModalVisible(false);
        formNumeroRequerimentoRef.current?.resetFields();
        formCredenciaisRef.current?.resetFields();
    }, []);

    const handleClickOpenNumeroRequerimento = useCallback(() => {
        if (!selectedBanco) return;
        setIsNumeroRequerimentoModalVisible(true);
        setTimeout(() => {
            formNumeroRequerimentoRef.current?.setFieldsValue({
                numeroRequerimento: selectedBanco.autorizacaoBancoVan?.numeroRequerimento || '',
            });
        }, 50);
    }, [selectedBanco]);

    const handleClickOpenCredenciais = useCallback(() => {
        if (!selectedBanco) return;
        setIsCredenciaisModalVisible(true);

        let clientId = '';
        let clientSecret = '';

        if (selectedBanco.autorizacaoBancoVan?.credencialBancoVan) {
            const cred = selectedBanco.autorizacaoBancoVan.credencialBancoVan;
            clientId = cred.clientId || '';
            clientSecret = cred.clientSecret || '';
        }

        setTimeout(() => {
            formCredenciaisRef.current?.setFieldsValue({
                clientId,
                clientSecret,
            });
        }, 50);
    }, [selectedBanco]);

    const handleClickOpenVisualizarArquivos = useCallback(() => {
        if (!selectedBanco) return;
        setIsVisualizarArquivosModalVisible(true);
    }, [selectedBanco]);

    const handleSaveNumeroRequerimento = useCallback(() => {
        formNumeroRequerimentoRef.current?.validateFields()
            .then(async (values) => {
                if (!selectedBanco || (!solicitacao && !isManutencao)) return;

                try {
                    setIsSubmittingNumeroRequerimento(true);

                    const autorizacaoId = selectedBanco.autorizacaoBancoVan?.id || selectedBanco.id;

                    if (onUpdateNumeroRequerimento && autorizacaoId) {
                        const updatedData = await onUpdateNumeroRequerimento({
                            solicitacaoId: solicitacao?.id,
                            orgaoId: solicitacao?.orgao?.id,
                            autorizacaoId,
                            numeroRequerimento: values.numeroRequerimento,
                        });

                        setBancosVan((prevState) => {
                            if (!prevState) return null;
                            const next: IBancoVanBancaria[] = prevState.map((b) => {
                                if (b.id === selectedBanco.id) {
                                    return {
                                        ...b,
                                        autorizacaoBancoVan: {
                                            ...(b.autorizacaoBancoVan || {
                                                id: autorizacaoId,
                                                createdAt: moment().toISOString(),
                                                numeroRequerimento: values.numeroRequerimento,
                                                credencialBancoVan: null,
                                            }),
                                            numeroRequerimento: values.numeroRequerimento,
                                            ...(updatedData?.autorizacaoBancoVan ? updatedData.autorizacaoBancoVan : {}),
                                        }
                                    };
                                }
                                return b;
                            });
                            if (onBancosVanChange) onBancosVanChange(next);
                            return next;
                        });
                    }

                    notification.success({
                        message: 'Sucesso',
                        description: 'Número do requerimento salvo com sucesso!',
                    });

                    handleCloseModals();
                } catch (error) {
                    EmitirErro(error, true, 'saveNumeroRequerimento_error');
                } finally {
                    setIsSubmittingNumeroRequerimento(false);
                }
            })
            .catch(() => {
                notification.error({
                    message: 'Erro',
                    description: 'Preencha os campos obrigatórios!',
                });
            });
    }, [selectedBanco, solicitacao, isManutencao, onUpdateNumeroRequerimento, onBancosVanChange, handleCloseModals]);

    const handleSaveCredenciais = useCallback(() => {
        formCredenciaisRef.current?.validateFields()
            .then(async (values) => {
                const autorizacaoId = selectedBanco?.autorizacaoBancoVan?.id || selectedBanco?.id;
                if (!selectedBanco || (!solicitacao && !isManutencao) || !autorizacaoId) return;

                try {
                    setIsSubmittingCredenciais(true);

                    const hasCredencial = !!selectedBanco.autorizacaoBancoVan?.credencialBancoVan;

                    let updatedData: IBancoVanBancaria | null = null;

                    if (hasCredencial && onUpdateCredenciais) {
                        updatedData = await onUpdateCredenciais({
                            solicitacaoId: solicitacao?.id,
                            orgaoId: solicitacao?.orgao?.id,
                            autorizacaoId,
                            clientId: values.clientId,
                            clientSecret: values.clientSecret,
                        });
                    } else if (onCreateCredenciais) {
                        updatedData = await onCreateCredenciais({
                            solicitacaoId: solicitacao?.id,
                            orgaoId: solicitacao?.orgao?.id,
                            autorizacaoId,
                            clientId: values.clientId,
                            clientSecret: values.clientSecret,
                        });
                    }

                    setBancosVan((prevState) => {
                        if (!prevState) return null;
                        const next: IBancoVanBancaria[] = prevState.map((b) => {
                            if (b.id === selectedBanco.id) {
                                return {
                                    ...b,
                                    autorizacaoBancoVan: {
                                        ...(b.autorizacaoBancoVan || {
                                            id: autorizacaoId,
                                            createdAt: moment().toISOString(),
                                            numeroRequerimento: '',
                                            credencialBancoVan: null,
                                        }),
                                        credencialBancoVan: {
                                            id: b.autorizacaoBancoVan?.credencialBancoVan?.id || 0,
                                            clientId: values.clientId,
                                            clientSecret: values.clientSecret,
                                            createdAt: b.autorizacaoBancoVan?.credencialBancoVan?.createdAt || moment().toISOString(),
                                            updatedAt: moment().toISOString(),
                                            ...(updatedData?.autorizacaoBancoVan?.credencialBancoVan || {}),
                                        },
                                        ...(updatedData?.autorizacaoBancoVan ? updatedData.autorizacaoBancoVan : {}),
                                    }
                                };
                            }
                            return b;
                        });
                        if (onBancosVanChange) onBancosVanChange(next);
                        return next;
                    });

                    notification.success({
                        message: 'Sucesso',
                        description: 'Credenciais salvas com sucesso!',
                    });

                    handleCloseModals();
                } catch (error) {
                    EmitirErro(error, true, 'saveCredenciaisVan_error');
                } finally {
                    setIsSubmittingCredenciais(false);
                }
            })
            .catch(() => {
                notification.error({
                    message: 'Erro',
                    description: 'Preencha os campos obrigatórios!',
                });
            });
    }, [selectedBanco, solicitacao, isManutencao, onCreateCredenciais, onUpdateCredenciais, onBancosVanChange, handleCloseModals]);

    const handleDeleteAutorizacao = useCallback(async () => {
        if (!selectedBanco || (!solicitacao && !isManutencao)) return;

        const autorizacaoId = selectedBanco.autorizacaoBancoVan?.id;
        const solicitacaoId = solicitacao?.id || 0;

        console.log('aqui', autorizacaoId, onDeleteAutorizacao)

        if (autorizacaoId && onDeleteAutorizacao) {
            try {
                await onDeleteAutorizacao({
                    autorizacaoId,
                    solicitacaoId,
                });

                setBancosVan((prevState) => {
                    if (!prevState) return null;
                    const next = prevState.map((b) => {
                        if (b.id === selectedBanco.id) {
                            return {
                                ...b,
                                autorizacaoBancoVan: b.autorizacaoBancoVan
                                    ? {
                                        ...b.autorizacaoBancoVan,
                                        credencialBancoVan: null,
                                    }
                                    : null,
                            };
                        }
                        return b;
                    });
                    if (onBancosVanChange) onBancosVanChange(next);
                    return next;
                });

                notification.success({
                    message: 'Sucesso',
                    description: 'Autorização deletada com sucesso!',
                });
            } catch (error) {
                EmitirErro(error, true, 'deleteAutorizacao_van_error');
            }
        }
    }, [selectedBanco, solicitacao, isManutencao, onDeleteAutorizacao, onBancosVanChange]);

    const handleVisualizarArquivo = useCallback(async (doc: IDocumentoVanBancaria) => {
        const autorizacaoId = selectedBanco?.autorizacaoBancoVan?.id || selectedBanco?.id;
        if (!autorizacaoId || (!solicitacao && !isManutencao) || !onGetDocumentoSignedUrl) return;

        try {
            setLoadingDocId(doc.id);
            const response = await onGetDocumentoSignedUrl({
                solicitacaoId: solicitacao?.id,
                orgaoId: solicitacao?.orgao?.id,
                autorizacaoId,
                documentoId: doc.id,
            });

            const linkUrl = response?.link || response?.url || (typeof response === 'string' ? response : null);

            if (linkUrl) {
                const link = document.createElement("a");
                link.href = linkUrl;
                link.setAttribute("download", "");
                link.target = "_blank";

                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                notification.warning({
                    message: 'Aviso',
                    description: 'Não foi possível obter o link do documento.',
                });
            }
        } catch (error) {
            EmitirErro(error, true, 'getDocumentoSignedUrl_error');
        } finally {
            setLoadingDocId(null);
        }
    }, [selectedBanco, solicitacao, isManutencao, onGetDocumentoSignedUrl]);

    const columns = [
        {
            title: 'Banco',
            dataIndex: 'nome',
            key: 'nome',
            filteredValue: filteredInfo.nome || null,
            ...getColumnSearchProps('nome', (record: IBancoVanBancaria) => record.nome),
            sorter: (a: IBancoVanBancaria, b: IBancoVanBancaria) => a.nome.localeCompare(b.nome),
            render: (nome: string, record: IBancoVanBancaria) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{nome}</span>
                </div>
            ),
        },
        {
            title: 'Número do Requerimento',
            dataIndex: 'numeroRequerimento',
            key: 'numeroRequerimento',
            filteredValue: filteredInfo.numeroRequerimento || null,
            ...getColumnSearchProps(
                'numeroRequerimento',
                (record: IBancoVanBancaria) => record.autorizacaoBancoVan?.numeroRequerimento || ''
            ),
            sorter: (a: IBancoVanBancaria, b: IBancoVanBancaria) => {
                const reqA = a.autorizacaoBancoVan?.numeroRequerimento || '';
                const reqB = b.autorizacaoBancoVan?.numeroRequerimento || '';
                return reqA.localeCompare(reqB);
            },
            render: (_: any, record: IBancoVanBancaria) => (
                record.autorizacaoBancoVan?.numeroRequerimento || '-'
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filteredValue: filteredInfo.status || null,
            filters: [
                { text: 'Não solicitado', value: 'Não solicitado' },
                { text: 'Solicitado', value: 'Solicitado' },
                { text: 'Autorizado', value: 'Autorizado' },
            ],
            onFilter: (value: any, record: IBancoVanBancaria) => {
                const status = record.autorizacaoBancoVan?.credencialBancoVan
                    ? 'Autorizado'
                    : record.autorizacaoBancoVan?.numeroRequerimento
                        ? 'Solicitado'
                        : 'Não solicitado';

                return value === status;
            },
            render: (_: any, record: IBancoVanBancaria) => {
                let cor = '';

                const status = record.autorizacaoBancoVan?.credencialBancoVan
                    ? 'Autorizado'
                    : record.autorizacaoBancoVan?.numeroRequerimento
                        ? 'Solicitado'
                        : 'Não solicitado';

                switch (status) {
                    case 'Não solicitado':
                        cor = '#D4D429';
                        break;
                    case 'Solicitado':
                        cor = '#2B98FF';
                        break;
                    case 'Autorizado':
                        cor = '#7CBC3B';
                        break;
                    default:
                        cor = '';
                        break;
                }

                return (
                    <span>
                        {status ? (
                            <Tag color={cor}>
                                <span style={{ fontWeight: 600 }}>
                                    {status}
                                </span>
                            </Tag>
                        ) : null}
                    </span>
                );
            },
        },
        {
            title: 'Solicitado em',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a: IBancoVanBancaria, b: IBancoVanBancaria) => {
                const timeA = a.autorizacaoBancoVan?.createdAt ? moment(a.autorizacaoBancoVan.createdAt).unix() : 0;
                const timeB = b.autorizacaoBancoVan?.createdAt ? moment(b.autorizacaoBancoVan.createdAt).unix() : 0;
                return timeA - timeB;
            },
            showSorterTooltip: false,
            render: (_: any, record: IBancoVanBancaria) => (
                record.autorizacaoBancoVan?.createdAt
                    ? moment(record.autorizacaoBancoVan.createdAt).format('DD/MM/YYYY HH:mm')
                    : '-'
            ),
        },
    ];

    const { documentosUltimos30Dias, documentosMaisAntigos, todosDocumentos } = useMemo(() => {
        const docs = selectedBanco?.autorizacaoBancoVan?.documentos || [];
        const limite30Dias = moment().subtract(30, 'days').startOf('day');

        const sorted = [...docs].sort((a, b) => {
            const timeA = a.createdAt ? moment(a.createdAt).unix() : 0;
            const timeB = b.createdAt ? moment(b.createdAt).unix() : 0;
            return timeB - timeA;
        });

        const ultimos30: IDocumentoVanBancaria[] = [];
        const maisAntigos: IDocumentoVanBancaria[] = [];

        sorted.forEach((doc) => {
            if (doc.createdAt && moment(doc.createdAt).isSameOrAfter(limite30Dias)) {
                ultimos30.push(doc);
            } else {
                maisAntigos.push(doc);
            }
        });

        return {
            documentosUltimos30Dias: ultimos30,
            documentosMaisAntigos: maisAntigos,
            todosDocumentos: sorted,
        };
    }, [selectedBanco]);

    const renderTabelaDocumentos = useCallback((documentos: IDocumentoVanBancaria[], emptyText: string) => {
        if (documentos.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={emptyText}
                    style={{ margin: '30px 0' }}
                />
            );
        }

        return (
            <Table
                rowKey={(record) => record.id}
                size="small"
                pagination={false}
                dataSource={documentos}
                columns={[
                    {
                        title: 'Arquivo',
                        key: 'arquivo',
                        render: (_: any, doc: IDocumentoVanBancaria) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FaFileAlt style={{ marginRight: 8, color: '#e74c3c' }} />
                                <span>{doc.nome || doc.fileName || `Documento #${doc.id}`}</span>
                            </div>
                        ),
                    },
                    {
                        title: 'Data de Envio',
                        key: 'createdAt',
                        render: (_: any, doc: IDocumentoVanBancaria) => (
                            doc.createdAt ? moment(doc.createdAt).format('DD/MM/YYYY HH:mm') : '-'
                        ),
                    },
                    {
                        title: "Ação",
                        key: "acao",
                        width: 50,
                        render: (_: any, doc: IDocumentoVanBancaria) => (
                            <div className="divAcao">
                                <Tooltip title="Visualizar arquivo" color="blue">
                                    <Button
                                        type="primary"
                                        icon={<EyeOutlined />}
                                        loading={loadingDocId === doc.id}
                                        onClick={() => handleVisualizarArquivo(doc)}
                                    />
                                </Tooltip>
                            </div>
                        ),
                    },
                ]}
            />
        );
    }, [loadingDocId, handleVisualizarArquivo]);

    return (
        <>
            <Col span={24}>
                <div
                    style={{
                        position: 'sticky',
                        top: 84,
                        zIndex: 2,
                        width: 'fit-content'
                    }}
                >
                    <Row>
                        <Col>
                            <Button
                                type="primary"
                                style={{ marginRight: 5, marginBottom: 5 }}
                                className="btn_warning_cewuyfgfvewofc"
                                onClick={handleClickOpenNumeroRequerimento}
                                disabled={!hasOneRowSelected}
                            >
                                Gerenciar número do requerimento
                            </Button>
                        </Col>

                        <Col>
                            <Button
                                type="primary"
                                style={{ marginRight: 5, marginBottom: 5 }}
                                className="btn_warning_cewuyfgfvewofc"
                                onClick={handleClickOpenCredenciais}
                                disabled={!enableButtonGerenciarCredenciais}
                            >
                                Gerenciar credenciais
                            </Button>
                        </Col>

                        {/* <Col>
                            <Popconfirm
                                title="Deseja excluir a autorização?"
                                onConfirm={handleDeleteAutorizacao}
                                onCancel={() => { }}
                                okText="Sim"
                                cancelText="Não"
                                disabled={!enableButtonGerenciarCredenciais}
                            >
                                <Button
                                    type="primary"
                                    danger
                                    disabled={!enableButtonGerenciarCredenciais}
                                    style={{ marginRight: 5, marginBottom: 5 }}
                                >
                                    <DeleteOutlined />
                                </Button>
                            </Popconfirm>
                        </Col> */}

                        <Col>
                            <Tooltip
                                title={
                                    !hasOneRowSelected
                                        ? "Selecione um banco"
                                        : !enableButtonVisualizarArquivos
                                            ? "Disponível após o cadastro das credenciais"
                                            : ""
                                }
                            >
                                <Button
                                    type="primary"
                                    style={{ marginRight: 5, marginBottom: 5 }}
                                    onClick={handleClickOpenVisualizarArquivos}
                                    disabled={!enableButtonVisualizarArquivos}
                                    icon={<FilePdfOutlined />}
                                >
                                    Visualizar arquivos
                                </Button>
                            </Tooltip>
                        </Col>

                        <Col>
                            <BotaoLimparFiltros
                                hasActiveFilters={hasActiveFilters}
                                onClick={handleClearAllFilters}
                            />
                        </Col>

                        <Col>
                            <BotaoLimparSelecao
                                hasSelectedRows={selectedRowKeys.length > 0}
                                onClick={handleResetRowSelection}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="headerAbaixodosBtnTableList_ihabdfvohewvbfopu" />

                <Table
                    rowKey={(record) => record.id}
                    style={{ marginTop: -34 }}
                    size="small"
                    onRow={(record) => ({
                        onClick: () => {
                            onclickTable(record);
                        },
                    })}
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={bancosVan || []}
                    pagination={{ locale: { items_per_page: '' } }}
                    locale={locale.Table}
                    loading={isLoading}
                    onChange={(pagination, filters, sorter, extra) => {
                        setFilteredInfo(filters || {});
                        handleResetRowSelection();
                    }}
                />
            </Col>

            {/* Modal 1: Gerenciar Número do Requerimento */}
            <Modal
                title={selectedBanco?.autorizacaoBancoVan?.numeroRequerimento ? "Editar número do requerimento" : "Cadastrar número do requerimento"}
                open={isNumeroRequerimentoModalVisible}
                onCancel={handleCloseModals}
                footer={null}
                width={700}
            >
                <Row gutter={[8, 8]}>
                    <Col xs={24}>
                        <CardInformacao
                            IconComponent={FaUniversity}
                            title="BANCO"
                            value={selectedBanco?.nome || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{ padding: '4px 6px' }}
                        />
                    </Col>
                </Row>

                <Form
                    ref={formNumeroRequerimentoRef}
                    style={{ marginTop: 15 }}
                    layout="vertical"
                >
                    <Form.Item
                        name="numeroRequerimento"
                        label="Número do Requerimento"
                        rules={[
                            {
                                required: true,
                                message: "Informe o número do requerimento!",
                            },
                        ]}
                    >
                        <Input placeholder="Informe o número do requerimento" />
                    </Form.Item>

                    <Row>
                        <Col span={24} style={{ textAlign: "end" }}>
                            <Button
                                style={{ marginRight: 10 }}
                                onClick={handleCloseModals}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={handleSaveNumeroRequerimento}
                                loading={isSubmittingNumeroRequerimento}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Modal 2: Gerenciar Credenciais VAN */}
            <Modal
                title={selectedBanco?.autorizacaoBancoVan?.credencialBancoVan ? "Editar credenciais VAN Bancária" : "Cadastrar credenciais VAN Bancária"}
                open={isCredenciaisModalVisible}
                onCancel={handleCloseModals}
                footer={null}
                width={800}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: !screens.md ? 'wrap' : 'nowrap',
                        rowGap: 8
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <Row gutter={[8, 8]}>
                            <Col xs={24} sm={12}>
                                <CardInformacao
                                    IconComponent={FaUniversity}
                                    title="BANCO"
                                    value={selectedBanco?.nome || "--"}
                                    iconSize={13}
                                    fontSize={12}
                                    style={{ padding: '4px 6px' }}
                                />
                            </Col>

                            <Col xs={24} sm={12}>
                                <CardInformacao
                                    IconComponent={FaFileSignature}
                                    title="NÚMERO DO REQUERIMENTO"
                                    value={selectedBanco?.autorizacaoBancoVan?.numeroRequerimento || "--"}
                                    iconSize={13}
                                    fontSize={12}
                                    style={{ padding: '4px 6px' }}
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                <Form
                    ref={formCredenciaisRef}
                    style={{ marginTop: 15 }}
                    layout="vertical"
                >
                    <Form.Item
                        name="clientId"
                        label="Client ID"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Informe o Client ID" rows={2} />
                    </Form.Item>

                    <Form.Item
                        name="clientSecret"
                        label="Client Secret"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Informe o Client Secret" rows={3} />
                    </Form.Item>

                    <Row>
                        <Col span={24} style={{ textAlign: "end" }}>
                            <Button
                                style={{ marginRight: 10 }}
                                onClick={handleCloseModals}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={handleSaveCredenciais}
                                loading={isSubmittingCredenciais}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Modal 3: Visualizar Arquivos Enviados */}
            <Modal
                title="Arquivos enviados"
                open={isVisualizarArquivosModalVisible}
                onCancel={handleCloseModals}
                footer={[
                    <Button key="fechar" type="primary" onClick={handleCloseModals}>
                        Fechar
                    </Button>
                ]}
                width={700}
            >
                <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                        <CardInformacao
                            IconComponent={FaUniversity}
                            title="BANCO"
                            value={selectedBanco?.nome || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{ padding: '4px 6px' }}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <CardInformacao
                            IconComponent={FaFileSignature}
                            title="NÚMERO DO REQUERIMENTO"
                            value={selectedBanco?.autorizacaoBancoVan?.numeroRequerimento || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{ padding: '4px 6px' }}
                        />
                    </Col>
                </Row>

                <Tabs
                    defaultActiveKey="ultimos-30-dias"
                    items={[
                        {
                            key: 'ultimos-30-dias',
                            label: (
                                <TabTitleCount
                                    descricao="Últimos 30 dias"
                                    count={documentosUltimos30Dias.length}
                                />
                            ),
                            children: renderTabelaDocumentos(
                                documentosUltimos30Dias,
                                'Nenhum arquivo enviado nos últimos 30 dias.'
                            ),
                        },
                        {
                            key: 'mais-antigos',
                            label: (
                                <TabTitleCount
                                    descricao="Mais de 30 dias"
                                    count={documentosMaisAntigos.length}
                                />
                            ),
                            children: renderTabelaDocumentos(
                                documentosMaisAntigos,
                                'Nenhum arquivo enviado com mais de 30 dias.'
                            ),
                        },
                        {
                            key: 'todos',
                            label: (
                                <TabTitleCount
                                    descricao="Todos"
                                    count={todosDocumentos.length}
                                />
                            ),
                            children: renderTabelaDocumentos(
                                todosDocumentos,
                                'Nenhum arquivo enviado até o momento.'
                            ),
                        },
                    ]}
                />
            </Modal>
        </>
    );
};
